import {
  ExpectedStayRow,
  ExpectedStaysCounts,
} from '../../../../interfaces/stays';
import { ExpectedRow } from '../../../../interfaces/visitor-pass';
import { clockTime } from '../../../../function-utils/building-time';
import { countOf } from '../../../../function-utils/plural';
import { nameAtoms, nameTokens } from '../../visitor-exit/visitor-exit-match';
import { PillSpec, signableNowCount } from './expected-model';

/**
 * THE SHORT-STAY LANE, AS RULES (Lobbero Stays plan S3) — what the desk's
 * Expected card and screen say about a row of
 * GET /stays/desk/buildings/:b/expected. Pure, so the menu, the screen and
 * their tests read ONE answer.
 *
 * THE SERVER DECIDES, THIS ONLY DESCRIBES. The window (early / late / a day
 * late), whether the online check-in is complete and why not, and whether the
 * guest is inside, are all the server's, on the server's clock. Nothing here
 * re-derives them from the tablet's clock. The only clock work is printing an
 * instant the server sent as HH:MM on the BUILDING's clock.
 */

/** The row's key on the page. Prefixed so it can never equal a pass row's key
 *  (the page holds one open confirm across both lists). */
export const stayKey = (row: ExpectedStayRow): string => `stay:${row.reservation_id}`;

/** The double-tap gate for a sign-in (the server locks per stay). */
export const staySignInGate = (row: ExpectedStayRow): string => `stay:${row.reservation_id}`;

export const stayDomId = (row: ExpectedStayRow): string => `expected-stay-${row.reservation_id}`;

/** "Ana" in "Short-stay guest · Ana" and "Confirm: sign Ana in". */
export const stayFirstName = (row: ExpectedStayRow): string =>
  (row.first_name || '').trim() || 'the guest';

/** The booking's name, first and last. */
export const stayFullName = (row: ExpectedStayRow): string =>
  `${row.first_name || ''} ${row.last_name || ''}`.trim() || stayFirstName(row);

/** The name on the guest's ID, once verified — what the desk compares. */
export const stayIdName = (row: ExpectedStayRow): string | null => {
  if (!row.id_name) return null;
  const name = `${row.id_name.first || ''} ${row.id_name.last || ''}`.trim();

  return name || null;
};

/** The one tap is on offer: the guest has not been signed in on this desk day. */
export const isStaySignable = (row: ExpectedStayRow): boolean =>
  row.arrival_state === 'not_arrived';

/** The guest is inside, and the arrival to close is known. */
export const isStaySignOutable = (row: ExpectedStayRow): boolean =>
  row.arrival_state === 'inside' && row.arrival_id != null;

const rank = (row: ExpectedStayRow): number =>
  row.arrival_state === 'not_arrived' ? 0 : row.arrival_state === 'inside' ? 1 : 2;

/**
 * THE SERVER'S ORDER, restated: guests still to arrive first, by when they
 * arrive, then by unit; those inside (and those already gone) last. Sorting
 * again means a list patched between reads never puts somebody handled above
 * the guests still to come.
 */
export const sortExpectedStays = (rows: ExpectedStayRow[]): ExpectedStayRow[] =>
  rows.slice().sort((a, b) => {
    const handled = rank(a) - rank(b);
    if (handled !== 0) return handled;
    const due = (Date.parse(a.arrival_at) || 0) - (Date.parse(b.arrival_at) || 0);
    if (due !== 0) return due;

    return (a.unit_label || '').localeCompare(b.unit_label || '', 'en', { numeric: true });
  });

/**
 * LINE 2 OF A ROW: the unit and when. `time` is printed inside a <time>
 * element that carries the instant (data-at), so `lead` + `time` + nothing is
 * the whole line.
 *   "1102 · Arriving 15:00"      the guest told us when
 *   "1102 · From 15:00"          the listing's check-in time stands in
 *   "1102 · Expected yesterday"  the arrival was on an earlier desk day
 *   "1102 · Back today"          signed out last night, back this evening
 */
export const stayWhen = (
  row: ExpectedStayRow,
  zone: string | null | undefined
): { lead: string; time: string | null } => {
  const unit = row.unit_label || '—';
  // A returning guest's window is 'next_day' on the server too; the return is
  // the fact the desk needs, so it is read first.
  if (row.returning) return { lead: `${unit} · Back today`, time: null };
  if (row.window_state === 'next_day') return { lead: `${unit} · Expected yesterday`, time: null };
  const time = clockTime(row.arrival_at, zone || undefined) || null;
  if (!time) return { lead: unit, time: null };

  return { lead: `${unit} · ${row.arrival_known ? 'Arriving' : 'From'} `, time };
};

/**
 * The online check-in, as a pill (plan S6, R-S6.30): every step done; the ID
 * side done but not the rest; or S3's "Check-in incomplete" (the reason
 * underneath).
 */
/** IS0 (R-IS0.6): the in-house check's word for the desk; never "verified". */
const ID_REVIEW_WORDS: Record<'needs_look' | 'checked' | 'read' | 'none', string> = {
  needs_look: 'ID needs a look',
  checked: 'ID read · checked',
  read: 'ID read',
  none: 'ID not given yet',
};

export const stayCheckinPill = (row: ExpectedStayRow): PillSpec => {
  // IS7 (R-IS7.2): a linked stay shows its ID status only; the host's other check-in
  // steps are not this desk's to read.
  if (row.linked) {
    if (row.id_review) {
      return { tone: row.id_review === 'needs_look' || row.id_review === 'none' ? 'warning' : 'success', text: ID_REVIEW_WORDS[row.id_review] };
    }

    return { tone: row.checkin_state === 'verified' ? 'success' : 'warning', text: row.checkin_state === 'verified' ? 'ID verified' : 'ID not verified' };
  }
  if (row.id_review) {
    const word = ID_REVIEW_WORDS[row.id_review];
    if (row.checkin_complete) {
      return { tone: row.id_review === 'needs_look' ? 'warning' : 'success', text: `Check-in complete · ${word}` };
    }
    if (row.checkin_state === 'verified') return { tone: 'warning', text: `${word} · check-in incomplete` };

    return { tone: 'warning', text: 'Check-in incomplete' };
  }
  if (row.checkin_complete) return { tone: 'success', text: 'Check-in complete · ID verified' };
  if (row.checkin_state === 'verified') return { tone: 'warning', text: 'ID verified · check-in incomplete' };

  return { tone: 'warning', text: 'Check-in incomplete' };
};

/**
 * What the guest told the host from their own page (plan S6, K-25), on the
 * building's clock: "Guest says they've arrived 18:40", "Asked for help". The
 * desk never sees the message itself.
 */
export const stayGuestLines = (row: ExpectedStayRow, zone: string | null | undefined): string[] => {
  const out: string[] = [];
  if (row.self_arrived_at) {
    const time = clockTime(row.self_arrived_at, zone || undefined);
    out.push(time ? `Guest says they've arrived ${time}` : "Guest says they've arrived");
  }
  if (row.help_open && row.help_open > 0) out.push('Asked for help');

  return out;
};

/** Why the check-in is incomplete, in a few words; null when it is complete. */
export const stayCheckinReason = (row: ExpectedStayRow): string | null => {
  if (row.checkin_state === 'verified') return null;
  if (row.id_review) {
    // IS0 (R-IS0.6): the in-house words.
    switch (row.checkin_reason) {
      case 'id_not_verified':
        return 'ID not given yet';
      case 'id_in_progress':
        return 'ID being added';
      case 'id_failed':
        return 'Asked to scan the ID again';
      case 'others_not_verified':
        return `${row.verified_count} of ${row.adults_expected ?? row.party_size} IDs done`;
      default:
        return null;
    }
  }
  switch (row.checkin_reason) {
    case 'id_not_verified':
      return 'ID not verified';
    case 'id_in_progress':
      return 'ID check in progress';
    case 'id_failed':
      return 'ID check failed';
    case 'others_not_verified':
      return `${row.verified_count} of ${row.adults_expected ?? row.party_size} IDs verified`;
    default:
      return null;
  }
};

/**
 * The window, as a pill — only for a guest still to arrive, and never for one
 * coming BACK (their 'next_day' is the return, not lateness).
 */
export const stayWindowPill = (row: ExpectedStayRow): PillSpec | null => {
  if (row.arrival_state !== 'not_arrived' || row.returning) return null;
  if (row.window_state === 'early') return { tone: 'warning', text: 'Early' };
  if (row.window_state === 'late') return { tone: 'warning', text: 'Late' };
  if (row.window_state === 'next_day') return { tone: 'warning', text: 'A day late' };

  return null;
};

/** "Signed in 15:04" / "Signed out 18:30", with the instant; null before arrival. */
export const stayArrival = (
  row: ExpectedStayRow,
  zone: string | null | undefined
): { lead: string; time: string; at: string } | null => {
  if (row.arrival_state === 'inside' && row.arrived_at) {
    return { lead: 'Signed in ', time: clockTime(row.arrived_at, zone || undefined), at: row.arrived_at };
  }
  if (row.arrival_state === 'left' && row.departed_at) {
    return { lead: 'Signed out ', time: clockTime(row.departed_at, zone || undefined), at: row.departed_at };
  }

  return null;
};

/** Which "sign in anyway?" ticks the confirm asks for. `force*` = the server
 *  asked for one the list did not know about yet (a 409 after the window or
 *  the check-in moved on). */
export const stayTicks = (
  row: ExpectedStayRow,
  force: { outside?: boolean; incomplete?: boolean } = {}
): { outside: boolean; incomplete: boolean } => ({
  outside: row.window_state !== 'in_window' || !!force.outside,
  incomplete: row.checkin_state === 'incomplete' || !!force.incomplete,
});

/** WHAT THE ONE TAP WILL WRITE, before it is written. */
export const describeStaySignIn = (row: ExpectedStayRow): string =>
  `Signs in ${stayFullName(row)} (Short-stay guest, unit ${row.unit_label || '—'}, ${countOf(
    row.party_size,
    'guest',
    'guests'
  )}).`;

/** IS7: "host: Lobbero Stays" on a linked stay; null on the building's own. */
export const stayHostTag = (row: ExpectedStayRow): string | null =>
  row.linked ? `host: ${row.host_kind || 'Lobbero Stays'}` : null;

/** The search box: every token must prefix something the row carries — the
 *  booking name, the name on the ID, the unit or the listing. */
export const filterExpectedStays = (
  rows: ExpectedStayRow[],
  query: string
): ExpectedStayRow[] => {
  const tokens = nameTokens(query);
  if (tokens.length === 0) return rows;

  return rows.filter(row => {
    const atoms = nameAtoms([
      `${row.first_name} ${row.last_name}`,
      stayIdName(row),
      row.unit_label,
      row.listing_name,
    ]);

    return tokens.every(token => atoms.some(atom => atom.startsWith(token)));
  });
};

/** A scanned document against the lane — by NAME only (the booking's or the
 *  one on the ID). It narrows; it never signs anybody in. */
export const matchScannedStays = (
  rows: ExpectedStayRow[],
  firstName: string,
  lastName: string
): ExpectedStayRow[] => {
  const tokens = nameTokens(`${firstName || ''} ${lastName || ''}`.trim());
  if (tokens.length === 0) return [];

  return rows.filter(row => {
    const atoms = nameAtoms([`${row.first_name} ${row.last_name}`, stayIdName(row)]);

    return tokens.every(token => atoms.some(atom => atom.startsWith(token)));
  });
};

/**
 * THE LANE ON THE SCREEN: the person holds stays:desk AND the lane's own
 * answer says Stays is on for this building. Null (not answered) is not on.
 */
export const staysLaneVisible = ({
  canDeskStays,
  liveStaysEnabled,
}: {
  canDeskStays: boolean;
  liveStaysEnabled: boolean | null;
}): boolean => canDeskStays && liveStaysEnabled === true;

/**
 * THE MAIN MENU'S SHARE OF THE EXPECTED CARD. Three answers, all required:
 *   - the person holds stays:desk (what the lane checks);
 *   - the building list says this building has Stays on;
 *   - the live lane has not since said it is OFF (the building list is cached
 *     for the session; the lane is not). Null leaves the list's word standing.
 */
export const expectedStaysCardVisible = ({
  canDeskStays,
  buildingStaysEnabled,
  liveStaysEnabled,
}: {
  canDeskStays: boolean;
  buildingStaysEnabled: boolean;
  liveStaysEnabled: boolean | null;
}): boolean => canDeskStays && buildingStaysEnabled && liveStaysEnabled !== false;

/**
 * THE MAIN-MENU BADGE: the pass visitors signable now plus the short-stay
 * guests the desk can sign in now. The lane's `signable_now` already leaves
 * out an EARLY guest (a number the desk cannot clear is not a badge), so its
 * `expected` — which counts them — is not used here.
 */
export const expectedBadgeCount = (
  passRows: ExpectedRow[] | null | undefined,
  staysCounts: ExpectedStaysCounts | null | undefined
): number => signableNowCount(passRows) + Math.max(0, Number(staysCounts?.signable_now) || 0);
